<template>
  <MultiSelectFilter
    :show-menu="showMenu"
    :title="title"
    :subtitle="subtitle"
    :numberOfSelectedValues="numberOfSelectedValues"
    @handleMenu="handleOpenMenu"
    @clearFilters="onClearFilters"
  >
    <BbPopover
      :model-value="showMenu"
      :hideCloseButton="true"
      location="bottom"
      contentClass="authored-filters-popup"
      width="57%"
      @update:modelValue="handleOpenMenu(false)"
    >
      <template #popover>
        <div class="popover-container">
          <div class="element-types-list">
            <ElementTypeCard
              v-for="element in productCountryListValues"
              :key="element.id"
              :nrOfElements="element?.values.length"
              :title="element.label"
              :id="element.id"
              :is-selected="selectedFilterType === element.id"
              @set-selected-type="handleSelectedType"
            />
          </div>
          <div class="elements-list">
            <ElementMultiselectList
              :selected-values="selectedValues[selectedFilterType]"
              :options="currentOptions"
              @update:values="updateFilters"
            />
          </div>
        </div>
      </template>
    </BbPopover>
  </MultiSelectFilter>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import useAuthoring from '~/composables/useAuthoring';
import ElementTypeCard from '~/components/conversation/authoring/productAndCountry/ElementTypeCard.vue';
import ElementMultiselectList from '~/components/conversation/authoring/productAndCountry/ElementMultiselectList.vue';
import MultiSelectFilter from '~/components/conversation/authoring/common/MultiSelectFilter.vue';

export default {
  components: {
    MultiSelectFilter,
    ElementMultiselectList,
    ElementTypeCard,
  },
  setup() {
    const { t } = useI18n();
    const {
      expandedFilters,
      setExpandedFilters,
      setProductAndCountryFilters,
      productAndCountryFilters,
      getProductAndCountryFilters,
    } = useAuthoring();

    const showMenu = ref(false);
    const selectedFilterType = ref('metadata.product');
    const selectedValues = ref({});
    const productCountryListValues = ref([]);

    const title = `${t('common.product')} & ${t('common.country')}  (${t('common.optional')})`;

    const currentOptions = computed(
      () => productCountryListValues.value.find((item) => item.id === selectedFilterType.value)?.values || [],
    );
    const numberOfSelectedValues = computed(() => Object.values(selectedValues.value).flat().length);
    const subtitle = computed(() =>
      numberOfSelectedValues.value ? `+${numberOfSelectedValues.value}` : t('author.filters.pickOneOrMore'),
    );

    const handleOpenMenu = async (value) => {
      showMenu.value = value;
      if (value) {
        setExpandedFilters(value);
      }
    };

    const handleSelectedType = (type) => {
      selectedFilterType.value = type;
    };

    const onClearFilters = () => {
      selectedValues.value = productCountryListValues.value.reduce((acc, item) => {
        acc[item.id] = [];
        return acc;
      }, {});
      setProductAndCountryFilters({});
    };

    const updateFilters = (newValues) => {
      selectedValues.value[selectedFilterType.value] = newValues;
      setProductAndCountryFilters(selectedValues.value);
    };

    watch(productAndCountryFilters, async () => {
      selectedValues.value = productAndCountryFilters.value;
    });

    onMounted(async () => {
      const productAndCountryFilterValues = await getProductAndCountryFilters();

      productCountryListValues.value = productAndCountryFilterValues;
      selectedValues.value = productCountryListValues.value?.reduce((acc, item) => {
        acc[item.id] = [];
        return acc;
      }, {});
    });

    return {
      t,
      expandedFilters,
      title,
      subtitle,
      showMenu,
      handleOpenMenu,
      onClearFilters,
      numberOfSelectedValues,
      selectedValues,
      productCountryListValues,
      handleSelectedType,
      selectedFilterType,
      currentOptions,
      updateFilters,
    };
  },
};
</script>

<style scoped lang="scss">
.popover-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-6;

  .element-types-list {
    display: flex;
    flex-direction: column;
    margin: 25px 0 20px 20px;
    flex: 1;
  }

  .elements-list {
    display: flex;
    height: fit-content;
    gap: $bb-spacing-4;
    flex-wrap: wrap;
    flex-direction: row;
    margin: 30px 0 6px 0;
    padding: 0 $bb-spacing-4;
    flex: 2;
    overflow: scroll;
    max-height: 300px;
  }
}
</style>
